import {ColumnOrGroupSpec, ColumnSpec, ColumnState, GridAutosizeOptions, GridModel} from '@xh/hoist/cmp/grid';
import {HoistModel, SizingMode, XH} from '@xh/hoist/core';
import {Store, StoreRecord} from '@xh/hoist/data';
import {runInAction} from '@xh/hoist/mobx';
import {isEqual, omit} from 'lodash';
import {buildColumnSpecs, buildColumnState, buildFilter, buildSortBy, buildSummaryData} from './GridSpecBuilder';
import {GridSpecModel} from './GridSpecModel';
import {ColumnLayoutEntry, leafColumns} from './GridSpecOps';
import {GridSpec, GridSpecColumnOrGroup, GridSpecSorter, isColumnGroup} from './GridSpecTypes';

export interface GridSpecBinderConfig {
    specModel: GridSpecModel;
    gridModel: GridModel;
    /** Host-provided base ColumnSpec per field - see BuildColumnsOptions. */
    baseColumn?: (field: string) => ColumnSpec;
    treeColumn?: string;
    /** Host hook to wrap the built columns - see BuildColumnsOptions. */
    wrapColumns?: (columns: ColumnOrGroupSpec[]) => ColumnOrGroupSpec[];
    /** Records to total in the summary row - defaults to the (filtered) store records. */
    summaryRecords?: (store: Store) => StoreRecord[];
    /**
     * True to only apply the spec to the grid, for a grid linked to another grid's spec. Linked grids each report a
     * slightly different layout, and feeding all of them back into one spec never settles.
     */
    specToGridOnly?: boolean;
}

/**
 * Keeps a GridModel in sync with a GridSpecModel, in both directions:
 *  - spec -> grid: applies each part of the spec with the narrowest GridModel update that works
 *    (column rebuild, column state, sort, flags...).
 *  - grid -> spec: user interactions on the grid itself (resize, reorder, hide via the column
 *    chooser, header sort) are turned into ops and dispatched with source 'grid'.
 *
 * Both directions compare against the other side before acting, so applying the spec to the grid
 * never echoes back as a new op, and vice versa.
 */
export class GridSpecBinder extends HoistModel {
    readonly specModel: GridSpecModel;
    readonly gridModel: GridModel;
    private opts: GridSpecBinderConfig;
    private lastSummary: object = null;
    /** The grid's own autosize options - used for any the spec leaves null. */
    private readonly hostAutosizeOptions: GridAutosizeOptions;

    constructor(config: GridSpecBinderConfig) {
        super();
        this.opts = config;
        this.specModel = config.specModel;
        this.gridModel = config.gridModel;
        this.hostAutosizeOptions = {...config.gridModel.autosizeOptions};

        const {specModel, gridModel} = this,
            {capabilities} = specModel;

        this.applyColumns(specModel.spec, true);

        this.addReaction(
            // spec -> grid
            {
                track: () => specModel.spec.columns,
                run: () => this.applyColumns(specModel.spec)
            },
            {
                track: () => [specModel.spec.rowBorders, specModel.spec.cellBorders, specModel.spec.stripeRows, specModel.spec.sizingMode],
                run: () => this.applyFlags(specModel.spec),
                fireImmediately: true
            },
            {
                track: () => [specModel.spec.autosizeMode, specModel.spec.autosizeFillMode],
                run: () => this.applyAutosizeOptions(specModel.spec),
                fireImmediately: true
            },
            {
                track: () => specModel.spec.totals,
                run: totals => (gridModel.showSummary = totals),
                fireImmediately: true
            },
            {
                track: () => specModel.spec.rowClassRules,
                run: () => gridModel.agApi?.redrawRows()
            },
            {
                track: () => [specModel.spec.totals, specModel.spec.columns, gridModel.store.lastUpdated],
                run: () => this.updateSummary(),
                fireImmediately: true
            }
        );

        // A host that sorts the grid itself keeps the spec out of it
        if (capabilities.sorting !== false) {
            this.addReaction({
                track: () => specModel.spec.sortBy,
                run: () => this.applySort(specModel.spec),
                fireImmediately: true
            });
        }

        // grid -> spec
        if (!config.specToGridOnly) {
            this.addReaction({
                track: () => gridModel.columnState,
                run: () => this.noteGridColumnState()
            });
            if (capabilities.sorting !== false) {
                this.addReaction({
                    track: () => gridModel.sortBy,
                    run: () => this.noteGridSort()
                });
            }
        }

        if (capabilities.grouping === 'flat') {
            this.addReaction({
                track: () => [specModel.spec.groupBy, specModel.spec.columns],
                run: () => this.applyGroupBy(specModel.spec),
                fireImmediately: true
            });
        }

        if (capabilities.filtering === 'flat') {
            this.addReaction({
                track: () => specModel.spec.filters,
                run: () => gridModel.store.setFilter(buildFilter(specModel.spec)),
                fireImmediately: true
            });
        }
    }

    //------------------------
    // spec -> grid
    //------------------------
    private lastColumnDefs: object = null;

    private applyColumns(spec: GridSpec, force = false) {
        const {gridModel} = this,
            defs = columnDefsSignature(spec.columns);

        let changed = false;
        runInAction(() => {
            if (force || !isEqual(defs, this.lastColumnDefs)) {
                const {baseColumn, treeColumn, wrapColumns} = this.opts;
                gridModel.setColumns(buildColumnSpecs(spec, {baseColumn, treeColumn, wrapColumns}));
                changed = true;
            }
            if (!this.layoutMatches(spec)) {
                gridModel.setColumnState(buildColumnState(spec));
                changed = true;
            }
        });
        this.lastColumnDefs = defs;

        // Managed autosize only runs on data load - also fit new columns and columns whose width was cleared
        if (changed && gridModel.isReady && gridModel.autosizeOptions.mode === 'managed') {
            gridModel.autosizeAsync({columns: gridModel.columnState.filter(s => !s.manuallySized).map(s => s.colId)});
        }
    }

    private applySort(spec: GridSpec) {
        if (!isEqual(gridSorters(this.gridModel), spec.sortBy)) {
            this.gridModel.setSortBy(buildSortBy(spec));
        }
    }

    private applyGroupBy(spec: GridSpec) {
        const {gridModel} = this,
            groupBy = spec.groupBy.filter(f => gridModel.getColumn(f));
        if (!isEqual(gridModel.groupBy ?? [], groupBy)) gridModel.setGroupBy(groupBy);
    }

    private applyFlags(spec: GridSpec) {
        const {gridModel} = this;
        gridModel.setRowBorders(spec.rowBorders);
        gridModel.setCellBorders(spec.cellBorders);
        gridModel.setStripeRows(spec.stripeRows);

        // A null sizing mode follows the app-wide mode (GridModel tracks it when created without one)
        if (spec.sizingMode || this.sizingModeSet) {
            gridModel.setSizingMode((spec.sizingMode ?? XH.sizingMode) as SizingMode);
            this.sizingModeSet = !!spec.sizingMode;
        }
    }

    private sizingModeSet = false;

    private applyAutosizeOptions(spec: GridSpec) {
        const {gridModel, hostAutosizeOptions: host} = this,
            prev = gridModel.autosizeOptions,
            mode = spec.autosizeMode ?? host.mode,
            fillMode = spec.autosizeFillMode ?? host.fillMode;
        if (mode === prev.mode && fillMode === prev.fillMode) return;

        gridModel.autosizeOptions = {...prev, mode, fillMode};

        // Outside managed mode Hoist flags every column with a width - its default width included - as
        // manually sized, and managed autosize skips those. Only widths the spec sets should stick.
        if (mode === 'managed') {
            const fixed = new Set(
                    leafColumns(spec)
                        .filter(c => c.width)
                        .map(c => c.field)
                ),
                changes = gridModel.columnState.filter(s => s.manuallySized && !fixed.has(s.colId)).map(s => ({colId: s.colId, manuallySized: false}));
            gridModel.updateColumnState(changes);
        }

        // Show the change right away - fit the columns when switching to managed or changing the fill
        if (gridModel.isReady && (mode === 'managed' || fillMode !== prev.fillMode)) gridModel.autosizeAsync();
    }

    private updateSummary() {
        const {specModel, gridModel, opts: config} = this,
            {spec} = specModel;
        if (!spec.totals) return;

        const records = config.summaryRecords?.(gridModel.store) ?? gridModel.store.records,
            labelField = config.treeColumn ?? leafColumns(spec).find(c => !c.hidden)?.field,
            data = buildSummaryData(spec, records, labelField);

        // Updating the summary bumps store.lastUpdated - skip no-op updates to avoid a loop
        if (isEqual(data, this.lastSummary) && gridModel.store.summaryRecords?.length) return;
        this.lastSummary = data;
        gridModel.store.updateData({rawSummaryData: data});
    }

    //------------------------
    // grid -> spec
    //------------------------
    private noteGridColumnState() {
        const {specModel, gridModel} = this,
            {spec} = specModel;
        if (this.layoutMatches(spec)) return;

        const layout: ColumnLayoutEntry[] = gridModel.columnState.map(s => ({
            field: s.colId,
            hidden: !!s.hidden,
            width: s.manuallySized && !s.hidden ? s.width : undefined,
            pinned: s.pinned ?? undefined
        }));

        specModel.dispatch([{op: 'setColumnLayout', layout}], {source: 'grid', label: 'Column layout', coalesceKey: 'grid-layout'});
    }

    private noteGridSort() {
        const {specModel, gridModel} = this,
            sortBy = gridSorters(gridModel);
        if (isEqual(sortBy, specModel.spec.sortBy)) return;

        specModel.dispatch([{op: 'setSort', sortBy}], {source: 'grid', coalesceKey: 'grid-sort'});
    }

    /** True if the grid's column order, visibility, pinning and manual widths match the spec. */
    private layoutMatches(spec: GridSpec): boolean {
        const {gridModel} = this,
            specCols = leafColumns(spec),
            state = gridModel.columnState;

        if (
            !isEqual(
                state.map(s => s.colId),
                specCols.map(c => c.field)
            )
        )
            return false;

        return specCols.every((c, idx) => {
            const s: ColumnState = state[idx];
            if (!!s.hidden !== !!c.hidden) return false;
            if ((s.pinned ?? null) !== (c.pinned ?? null)) return false;
            // A grid whose columns can't be resized keeps no width in its state - nothing to compare
            if (c.hidden || c.flex || !s.manuallySized || s.width == null) return true;
            // Compare against the column's own width when the spec doesn't set one
            const expected = c.width ?? gridModel.getColumn(c.field)?.width;
            return !expected || Math.round(s.width) === Math.round(expected);
        });
    }
}

/** Column definitions minus layout (order within a parent, width, visibility, pinning). */
function columnDefsSignature(columns: GridSpecColumnOrGroup[]): object {
    const strip = c => omit(c, 'width', 'hidden', 'pinned'),
        byField = (a, b) => (a.field ?? a.groupId).localeCompare(b.field ?? b.groupId);
    return columns.map(c => (isColumnGroup(c) ? {...c, children: c.children.map(strip).sort(byField)} : strip(c))).sort(byField);
}

function gridSorters(gridModel: GridModel): GridSpecSorter[] {
    return gridModel.sortBy.filter(s => !s.colId?.startsWith('ag-Grid')).map(({colId, sort, abs}) => (abs ? {colId, sort, abs: true} : {colId, sort}));
}
